import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  getDlqStats,
  getDlqMessages,
  reprocessMessages,
  reprocessAllMessages,
  getReprocessJobStatus,
  deleteMessages,
  purgeQueue,
  getDlqAuditList,
  getDlqAuditStats,
  batchUpdateDlqAudit,
  syncDlqMessages,
} from '@/api/dlq-api';
import type {
  QueueName,
  ReprocessRequest,
  ReprocessAllRequest,
  DeleteRequest,
  PurgeRequest,
  DlqAuditListParams,
  DlqAuditStatus,
  BatchUpdateDlqAuditRequest,
} from '@/types/dlq';

export function useDlqStats() {
  return useQuery({
    queryKey: ['dlq-stats'],
    queryFn: getDlqStats,
    refetchInterval: 30_000,
  });
}

export function useDlqMessages(queue: QueueName, limit?: number) {
  return useQuery({
    queryKey: ['dlq-messages', queue, limit],
    queryFn: () => getDlqMessages(queue, limit),
  });
}

export function useReprocessMessages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: ReprocessRequest) => reprocessMessages(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-messages'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-audit'] });
    },
  });
}

export function useReprocessAllMessages() {
  return useMutation({
    mutationFn: (req: ReprocessAllRequest) => reprocessAllMessages(req),
  });
}

/**
 * Poll a reprocess job status every 2 seconds while the job is running.
 * Returns undefined when no jobId is provided (idle state).
 */
export function useReprocessJobStatus(jobId: string | null) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['dlq-reprocess-job', jobId],
    queryFn: () => getReprocessJobStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') {
        // Job done — invalidate DLQ queries so the table refreshes
        queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
        queryClient.invalidateQueries({ queryKey: ['dlq-messages'] });
        queryClient.invalidateQueries({ queryKey: ['dlq-audit'] });
        return false;
      }
      return 2000; // Poll every 2s while running
    },
  });
}

export function useDeleteMessages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: DeleteRequest) => deleteMessages(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-messages'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-audit'] });
    },
  });
}

export function usePurgeQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: PurgeRequest) => purgeQueue(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-messages'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-audit'] });
    },
  });
}

// ── DLQ Audit Hooks ──

export function useDlqAuditStats() {
  return useQuery({
    queryKey: ['dlq-audit', 'stats'],
    queryFn: getDlqAuditStats,
    refetchInterval: 30_000,
  });
}

export function useDlqAuditList(params: DlqAuditListParams = {}) {
  return useQuery({
    queryKey: ['dlq-audit', 'list', params],
    queryFn: () => getDlqAuditList(params),
  });
}

export function useDlqAuditInfinite(params: { queue?: QueueName; status?: DlqAuditStatus; entityType?: string; entityId?: string; limit?: number } = {}) {
  return useInfiniteQuery({
    queryKey: ['dlq-audit', 'infinite', params],
    queryFn: ({ pageParam }) => getDlqAuditList({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function useBatchUpdateDlqAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: BatchUpdateDlqAuditRequest) => batchUpdateDlqAudit(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-audit'] });
    },
  });
}

export function useSyncDlqMessages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (queue: QueueName) => syncDlqMessages(queue),
    onSuccess: () => {
      // Remove infinite query cache to force fresh reload from page 1
      queryClient.removeQueries({ queryKey: ['dlq-audit', 'infinite'] });
      // Invalidate stats and other audit queries
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-audit', 'stats'] });
    },
  });
}
